import { useCallback } from 'react';
import { parseVerification, type Verification } from '@/utils/verification';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  clearAuthDataAction,
  setDataAndTokenAction,
  setCustomerIdAction,
} from '@/redux/slicers/auth.slice';
import { Messages } from '@/constants/message';
import { useRootSelector } from './selector.hook';
import {
  setToken,
  setCustomerId,
  removeCustomerId,
  setRefreshToken,
  removeToken,
  removeRefreshToken,
  getToken,
  getRefreshToken,
  getCustomerId,
  isTokenExpired,
  isRefreshTokenExpired,
  setTwoFactorToken,
  getTwoFactorToken,
  removeTwoFactorToken,
  setResetToken,
  getResetToken,
  removeResetToken,
  setKycStatus,
  removeKycStatus,
  getKycStatus,
} from '@/utils/common';
import { useApi, useCaller } from '@/hooks/api.hook';
import { UserInviteRegister, UserRegister } from '@/types/user';
import { refreshAccessToken, waitForTokenRefresh, isTokenRefreshing } from '@/utils/axios';
import { store } from '@/redux/store';
import { resetEntityContext } from '@/redux/slicers/entity-context.slice';
import { resetNotificationFeedAction } from '@/redux/slicers/notification-feed.slice';
import { resetNotificationSettingsAction } from '@/redux/slicers/notification-settings.slice';

type kycStatusType = 'NotKyc' | 'UnderReview' | 'Approved' | 'Rejected';

type IdentityTokens = {
  accessToken: string;
  refreshToken: string;
};

type IdentityLoginResult =
  | {
      requiresMfa: true;
      verification: Verification;
    }
  | {
      requiresMfa: false;
      tokens: IdentityTokens;
    };

type IdentityRegisterPayload = {
  email: string;
  password: string;
  displayName: string;
  phoneNumber: string;
  referralCode?: string;
};

type IdentityRegisterResult = {
  email: string;
  verification: Verification | null;
  userId: string;
};

type IdentityApiError = {
  response?: {
    data?: {
      errors?: Array<{ code?: string; message?: string }>;
      detail?: string;
    };
  };
};

const getIdentityApiError = (error: unknown) => {
  const identityError = error as IdentityApiError;
  return {
    apiError: identityError.response?.data?.errors?.[0],
    detail: identityError.response?.data?.detail,
  };
};

// Flag để tránh gọi fetchProfile nhiều lần cùng lúc (module level)
let isFetchingProfile = false;

// Wrapper function để sử dụng refreshAccessToken từ axios.ts
// Đảm bảo chỉ có 1 nơi gọi API refresh token
const globalRefreshToken = async () => {
  try {
    const newAccessToken = await refreshAccessToken();

    if (newAccessToken) {
      const newRefreshToken = getRefreshToken();

      return {
        success: true,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken || '',
      };
    }

    throw new Error('Refresh failed');
  } catch (error) {
    console.error('❌ Global refresh error:', error);
    throw error;
  }
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const api = useApi('');
  const caller = useCaller();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useRootSelector((state) => state.auth);

  const identityLogin = useCallback(
    async (identifier: string, password: string): Promise<IdentityLoginResult> => {
      try {
        const { data } = await api.post(
          '/v1/identity/auth/login',
          { identifier, password },
          { skipAuth: true },
        );
        const login = data?.data;

        if (login?.requiresMfa) {
          return {
            requiresMfa: true,
            verification: parseVerification(login.verification),
          };
        }

        if (!login?.tokens?.accessToken) {
          throw new Error('Login failed. Please try again.');
        }

        return {
          requiresMfa: false,
          tokens: login.tokens,
        };
      } catch (error) {
        const { apiError, detail } = getIdentityApiError(error);
        const message =
          apiError?.code === 'identity.invalid_credentials'
            ? 'Incorrect email or password.'
            : apiError?.message ??
              detail ??
              'Login failed. Please try again.';

        throw Object.assign(new Error(message), { code: apiError?.code });
      }
    },
    [api],
  );

  const verifyIdentityLoginMfa = useCallback(
    async (identifier: string, code: string, challengeId: string): Promise<IdentityTokens> => {
      try {
        const { data } = await api.post(
          '/v1/identity/auth/login/verify',
          { identifier, code, challengeId },
          { skipAuth: true },
        );
        // The verify endpoint may return tokens directly or nested under `tokens`.
        const tokens = data?.data?.tokens ?? data?.data;

        if (!tokens?.accessToken) {
          throw new Error('Verification failed. Please try again.');
        }

        return tokens;
      } catch (error) {
        const { apiError } = getIdentityApiError(error);
        const message =
          apiError?.code === 'identity.invalid_credentials' ||
          apiError?.code === 'identity.account_token_invalid'
            ? 'Invalid code. Check your authenticator app and try again.'
            : apiError?.message ?? 'Verification failed. Please try again.';

        throw Object.assign(new Error(message), { code: apiError?.code });
      }
    },
    [api],
  );

  const requestIdentityOtp = useCallback(
    async (destination: string, challengeId?: string): Promise<Verification> => {
      try {
        const { data } = await api.post(
          '/v1/identity/tokens/request',
          {
            purpose: 'email_verification',
            channel: 'email',
            destination: destination.trim(),
            format: 'code',
            challengeId,
          },
          { skipAuth: true },
        );
        return parseVerification(data?.data?.verification);
      } catch (error) {
        const { apiError, detail } = getIdentityApiError(error);
        throw new Error(
          apiError?.message ?? detail ?? 'Could not resend the code. Please try again.',
        );
      }
    },
    [api],
  );

  const resendIdentityLoginMfa = useCallback(async (challengeId: string): Promise<Verification> => {
    try {
      const { data } = await api.post('/v1/identity/auth/login/otp/request', { challengeId }, { skipAuth: true });
      return parseVerification(data?.data?.verification);
    } catch (error) {
      const { apiError, detail } = getIdentityApiError(error);
      throw new Error(apiError?.message ?? detail ?? 'Could not resend the code. Please try again.');
    }
  }, [api]);

  const requestPasswordResetOtp = useCallback(
    async (destination: string): Promise<void> => {
      try {
        await api.post(
          '/v1/identity/tokens/request',
          {
            purpose: 'password_reset',
            channel: 'email',
            destination: destination.trim(),
          },
          { skipAuth: true },
        );
      } catch (error) {
        const { apiError, detail } = getIdentityApiError(error);
        throw new Error(
          apiError?.message ?? detail ?? 'Could not send the reset code. Please try again.',
        );
      }
    },
    [api],
  );

  const verifyIdentityEmail = useCallback(
    async (destination: string, code: string, challengeId?: string): Promise<void> => {
      try {
        await api.post(
          '/v1/identity/tokens/verify-email',
          {
            destination: destination.trim(),
            code,
            challengeId,
          },
          { skipAuth: true },
        );
      } catch (error) {
        const { apiError, detail } = getIdentityApiError(error);
        throw new Error(
          apiError?.message ?? detail ?? 'Verification failed. Please try again.',
        );
      }
    },
    [api],
  );

  const resetIdentityPassword = useCallback(
    async (token: string, newPassword: string): Promise<void> => {
      try {
        await api.post(
          '/v1/identity/tokens/reset-password',
          {
            token,
            newPassword,
          },
          { skipAuth: true },
        );
      } catch (error) {
        const { apiError, detail } = getIdentityApiError(error);
        throw new Error(
          apiError?.message ?? detail ?? 'Could not reset your password. Please try again.',
        );
      }
    },
    [api],
  );

  const clearData = useCallback(() => {
    removeToken();
    removeRefreshToken();
    removeCustomerId();
    removeKycStatus();
    dispatch(clearAuthDataAction());
    dispatch(resetEntityContext());
    dispatch(resetNotificationFeedAction());
    dispatch(resetNotificationSettingsAction());
    navigate('/login');
  }, [dispatch, navigate]);

  const fetchProfile = useCallback(async () => {
    // Nếu đang fetch, bỏ qua
    if (isFetchingProfile) {
      return;
    }

    // Check xem token có hết hạn không
    const token = getToken();
    if (!token) {
      return;
    }

    // Check xem đã có user chưa (tránh gọi không cần thiết)
    // Lấy từ Redux store trực tiếp
    const currentUser = store.getState().auth.user;
    if (currentUser) {
      return;
    }

    isFetchingProfile = true;

    try {
      // Nếu token hết hạn và đang refresh, đợi refresh xong
      if (isTokenExpired(token) && isTokenRefreshing()) {
        const newToken = await waitForTokenRefresh();
        if (!newToken) {
          // Refresh thất bại, không call profile
          console.warn('Token refresh failed, skipping profile call');
          return;
        }
      }

      // Nếu token hết hạn nhưng chưa refresh, đợi một chút để axios interceptor xử lý
      if (isTokenExpired(token) && !isTokenRefreshing()) {
        // Đợi một chút để axios interceptor có thể bắt 401 và refresh
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check lại xem có đang refresh không
        if (isTokenRefreshing()) {
          const newToken = await waitForTokenRefresh();
          if (!newToken) {
            console.warn('Token refresh failed, skipping profile call');
            return;
          }
        }
      }

      // Enterprise identity: GET /v1/identity/me → { data: { userId, email, displayName, ... } }.
      const body = await caller(() => api.get('/v1/identity/me'), {
        loadingKey: 'profile-loading',
      });
      const me = body?.data ?? body?.content;
      if (me?.userId) {
        const displayName: string = me.displayName ?? '';
        const [firstName, ...rest] = displayName.split(' ');
        dispatch(
          setDataAndTokenAction({
            user: {
              id: me.userId,
              email: me.email,
              firstName: firstName || undefined,
              lastName: rest.join(' ') || undefined,
              preferredName: displayName || me.email,
              phoneNumber: me.phoneNumber || undefined,
              twoFactorEnabled: !!me.twoFactorEnabled,
            },
          }),
        );
      }
    } finally {
      isFetchingProfile = false;
    }
  }, [api, caller, dispatch]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/v1/api/auth/customer/login`, {
            email,
            password,
          }),
        {
          loadingKey: 'login-loading',
          successMessage: Messages.LOGIN_SUCCESS,
        },
      );

      if (succeeded) {
        let token = content?.accessToken;
        let refreshToken = content?.refreshToken;

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);

        // Check xem token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API
              const newAccessToken = await refreshAccessToken();

              if (newAccessToken) {
                // Refresh thành công -> lưu token mới vào local (thay thế cũ)
                token = newAccessToken;
                const newRefreshToken = getRefreshToken(); // Lấy refresh token mới từ localStorage (đã được set trong refreshAccessToken)

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                if (newRefreshToken) {
                  setRefreshToken(newRefreshToken);
                  refreshToken = newRefreshToken;
                }

                // Update Redux với token mới
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                clearData();
                return;
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after login:', error);
              clearData();
              return;
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            clearData();
            return;
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại và kycStatus từ login response
          const kycStatus = content?.kycStatus;
          if (kycStatus) {
            setKycStatus(kycStatus);
          }
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
              kycStatus: kycStatus || null,
            }),
          );
        }

        // Sau khi đảm bảo token hợp lệ, mới call profile
        // Đảm bảo token đã được lưu vào localStorage trước khi call profile
        // Đọc lại token từ localStorage để verify
        const savedToken = getToken();
        if (savedToken) {
          // Token đã được lưu -> call profile
          await fetchProfile();

          // Lưu kycStatus từ login response vào Redux và localStorage
          const kycStatus = content?.kycStatus;
          if (kycStatus) {
            setKycStatus(kycStatus);
          }
          dispatch(
            setDataAndTokenAction({
              kycStatus: kycStatus || null,
            }),
          );

          // Check redirectUrl và navigate accordingly
          const redirectUrl = content?.redirectUrl;
          if (redirectUrl) {
            // Nếu có redirectUrl, điều hướng đến URL đó
            navigate(redirectUrl);
          } else {
            // Chỉ khi redirectUrl là null thì mới vào dashboard
            navigate('/dashboard');
          }
        } else {
          // Token chưa được lưu -> đợi một chút rồi thử lại
          await new Promise((resolve) => setTimeout(resolve, 100));
          const retryToken = getToken();
          if (retryToken) {
            await fetchProfile();

            // Lưu kycStatus từ login response vào Redux
            dispatch(
              setDataAndTokenAction({
                kycStatus: content?.kycStatus,
              }),
            );

            // Check redirectUrl và navigate accordingly
            const redirectUrl = content?.redirectUrl;
            if (redirectUrl) {
              // Nếu có redirectUrl, điều hướng đến URL đó
              navigate(redirectUrl);
            } else {
              // Chỉ khi redirectUrl là null thì mới vào dashboard
              navigate('/dashboard');
            }
          } else {
            console.error('Token not saved after login');
            clearData();
          }
        }
      }
    },
    [api, caller, clearData, dispatch, fetchProfile, navigate],
  );

  const loginCapcha = useCallback(
    async (email: string, password: string, turnstileToken: string) => {
      const { content, succeeded } = await caller(
        () => {
          return api.post(`/v2/api/auth/customer/login`, {
            email,
            password,
            turnstileToken,
          });
        },
        {
          loadingKey: 'login-loading',
          successMessage: Messages.LOGIN_SUCCESS,
        },
      );

      if (succeeded) {
        const customerId = content?.customerId;

        let token = content?.accessToken;
        let refreshToken = content?.refreshToken;
        setCustomerId(customerId);
        // Cập nhật customerId vào Redux để các service có thể sử dụng ngay
        dispatch(setCustomerIdAction(customerId));

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);

        // Check xem token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API
              const newAccessToken = await refreshAccessToken();

              if (newAccessToken) {
                // Refresh thành công -> lưu token mới vào local (thay thế cũ)
                token = newAccessToken;
                const newRefreshToken = getRefreshToken(); // Lấy refresh token mới từ localStorage (đã được set trong refreshAccessToken)

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                if (newRefreshToken) {
                  setRefreshToken(newRefreshToken);
                  refreshToken = newRefreshToken;
                }

                // Update Redux với token mới và kycStatus từ login response
                const kycStatus = content?.kycStatus;
                if (kycStatus) {
                  setKycStatus(kycStatus);
                }
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                    kycStatus: kycStatus || null,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                clearData();
                return;
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after login:', error);
              clearData();
              return;
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            clearData();
            return;
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại và kycStatus từ login response
          const kycStatus = content?.kycStatus;
          if (kycStatus) {
            setKycStatus(kycStatus);
          }
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
              kycStatus: kycStatus || null,
            }),
          );
        }
      }
    },
    [api, caller, clearData, dispatch, fetchProfile, navigate],
  );

  const loginCapchaV3 = useCallback(
    async (email: string, password: string, turnstileToken: string) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/v3/api/auth/customer/login`, {
            email,
            password,
            turnstileToken,
          }),
        {
          loadingKey: 'login-loading',
          successMessage: Messages.LOGIN_SUCCESS,
        },
      );

      if (succeeded) {
        // Nếu yêu cầu 2FA, trả về thông tin 2FA thay vì set token
        if (content?.requiresTwoFactor) {
          if (content.twoFactorToken) {
            setTwoFactorToken(content.twoFactorToken);
          }
          return {
            succeeded: true,
            requiresTwoFactor: true,
            twoFactorToken: content.twoFactorToken,
            message: content.message,
            expiresInMinutes: content.expiresInMinutes,
            timeResent: content.timeResent,
            userId: content.userId,
            email: content.email,
          };
        }

        // Nếu không yêu cầu 2FA, xử lý như login bình thường
        let token = content?.accessToken;
        let refreshToken = content?.refreshToken;

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);

        // Check xem token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API
              const newAccessToken = await refreshAccessToken();

              if (newAccessToken) {
                // Refresh thành công -> lưu token mới vào local (thay thế cũ)
                token = newAccessToken;
                const newRefreshToken = getRefreshToken(); // Lấy refresh token mới từ localStorage (đã được set trong refreshAccessToken)

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                if (newRefreshToken) {
                  setRefreshToken(newRefreshToken);
                  refreshToken = newRefreshToken;
                }

                // Update Redux với token mới
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                clearData();
                return;
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after login:', error);
              clearData();
              return;
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            clearData();
            return;
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
            }),
          );
        }

        // Sau khi đảm bảo token hợp lệ, mới call profile
        // Đảm bảo token đã được lưu vào localStorage trước khi call profile
        // Đọc lại token từ localStorage để verify
        const savedToken = getToken();
        if (savedToken) {
          // Token đã được lưu -> call profile
          await fetchProfile();

          // Check redirectUrl và navigate accordingly
          const redirectUrl = content?.redirectUrl;
          if (redirectUrl) {
            // Nếu có redirectUrl, điều hướng đến URL đó
            navigate(redirectUrl);
          } else {
            // Chỉ khi redirectUrl là null thì mới vào dashboard
            navigate('/dashboard');
          }
        } else {
          // Token chưa được lưu -> đợi một chút rồi thử lại
          await new Promise((resolve) => setTimeout(resolve, 100));
          const retryToken = getToken();
          if (retryToken) {
            await fetchProfile();

            // Check redirectUrl và navigate accordingly
            const redirectUrl = content?.redirectUrl;
            if (redirectUrl) {
              // Nếu có redirectUrl, điều hướng đến URL đó
              navigate(redirectUrl);
            } else {
              // Chỉ khi redirectUrl là null thì mới vào dashboard
              navigate('/dashboard');
            }
          } else {
            console.error('Token not saved after login');
            clearData();
          }
        }

        return {
          succeeded: true,
          requiresTwoFactor: false,
        };
      }

      return {
        succeeded: false,
        requiresTwoFactor: false,
      };
    },
    [api, caller, clearData, dispatch, fetchProfile, navigate],
  );

  const register = useCallback(
    async ({
      email,
      password,
      displayName,
      phoneNumber,
      referralCode,
    }: IdentityRegisterPayload): Promise<IdentityRegisterResult> => {
      try {
        const normalizedReferralCode = referralCode?.trim();
        const { data } = await api.post(
          '/v1/identity/auth/register',
          {
            email: email.trim(),
            password,
            displayName: displayName.trim(),
            phoneNumber: phoneNumber.trim(),
            ...(normalizedReferralCode ? { referralCode: normalizedReferralCode } : {}),
          },
          { skipAuth: true },
        );
        const registration = data?.data;

        if (!registration?.userId) {
          throw new Error('Registration failed. Please try again.');
        }

        return { userId: String(registration.userId), email: registration.email, verification: registration.verification ? parseVerification(registration.verification) : null };
      } catch (error) {
        const { apiError, detail } = getIdentityApiError(error);
        throw new Error(
          apiError?.message ?? detail ?? 'Registration failed. Please try again.',
        );
      }
    },
    [api],
  );

  const registerCapcha = useCallback(
    async (data: UserRegister, turnstileToken: string) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/v2/api/auth/customer/register`, {
            ...data,
            turnstileToken,
          }),
        {
          loadingKey: 'register-loading',
          successMessage: Messages.REGISTER_SUCCESS,
        },
      );

      return { succeeded, content };
    },
    [api, caller],
  );

  const inviteRegister = useCallback(
    async (data: UserInviteRegister) => {
      const { content, succeeded } = await caller(
        () => api.post(`/v1/api/invite/${data.inviteCode}/accept`, data),
        {
          loadingKey: 'invite-register-loading',
          successMessage: Messages.REGISTER_SUCCESS,
        },
      );

      return { succeeded, content };
    },
    [api, caller],
  );

  const verifyTwoFactor = useCallback(
    async (code: string) => {
      const twoFactorToken = getTwoFactorToken();

      if (!twoFactorToken) {
        throw new Error('Two factor token not found');
      }

      const { content, succeeded } = await caller(
        () =>
          api.post(`/v3/api/auth/customer/login/verify-2fa`, {
            twoFactorToken,
            code,
          }),
        {
          loadingKey: 'verify-loading',
          successMessage: Messages.LOGIN_SUCCESS,
        },
      );

      if (succeeded) {
        // Xóa twoFactorToken khỏi session storage sau khi verify thành công
        removeTwoFactorToken();

        let token = content?.accessToken;
        let refreshToken = content?.refreshToken;
        const customerId = content?.customerId;
        const kycStatus = content?.kycStatus;

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);
        if (kycStatus) {
          setKycStatus(kycStatus);
        }

        // Lưu customerId vào localStorage và Redux ngay lập tức
        if (customerId) {
          setCustomerId(customerId);
          dispatch(setCustomerIdAction(customerId));
        }

        // Check xem token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API
              const newAccessToken = await refreshAccessToken();

              if (newAccessToken) {
                // Refresh thành công -> lưu token mới vào local (thay thế cũ)
                token = newAccessToken;
                const newRefreshToken = getRefreshToken(); // Lấy refresh token mới từ localStorage (đã được set trong refreshAccessToken)

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                if (newRefreshToken) {
                  setRefreshToken(newRefreshToken);
                  refreshToken = newRefreshToken;
                }

                // Update Redux với token mới
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                    kycStatus: kycStatus || null,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                clearData();
                return { succeeded: false };
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after verify:', error);
              clearData();
              return { succeeded: false };
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            clearData();
            return { succeeded: false };
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
              kycStatus: kycStatus || null,
            }),
          );
        }

        await fetchProfile();

        return {
          succeeded: true,
          content,
        };
      }

      return {
        succeeded: false,
        content,
      };
    },
    [api, caller, clearData, dispatch, fetchProfile],
  );

  const resendOtp = useCallback(async () => {
    const twoFactorToken = getTwoFactorToken();

    if (!twoFactorToken) {
      throw new Error('Two factor token not found');
    }

    const { content, succeeded } = await caller(
      () =>
        api.post(`/v3/api/auth/customer/login/resend-otp`, {
          twoFactorToken,
        }),
      {
        loadingKey: 'resend-otp-loading',
        successMessage: 'Verification code has been resent to your email',
      },
    );

    return { succeeded, timeResent: content?.timeResent };
  }, [api, caller]);

  // DEPRECATED: refreshToken function removed
  // Axios interceptor now handles all token refresh automatically
  // This prevents duplicate refresh calls and race conditions

  const checkAuthStatus = useCallback(async () => {
    try {
      const token = getToken();
      const refreshToken = getRefreshToken();

      // Nếu không có token, user chưa đăng nhập
      if (!token) {
        dispatch(clearAuthDataAction());
        return false;
      }

      // Nếu token còn hạn, user đã đăng nhập
      if (!isTokenExpired(token)) {
        // Đồng bộ customerId và kycStatus từ localStorage vào Redux khi app khởi động lại
        const customerId = getCustomerId();
        const kycStatus = getKycStatus();
        if (customerId) {
          dispatch(setCustomerIdAction(customerId));
        }
        dispatch(
          setDataAndTokenAction({
            isAuthenticated: true,
            token,
            refreshToken,
            kycStatus: kycStatus as kycStatusType | null,
          }),
        );
        // Không cần fetch profile ở đây vì đã có trong login flow hoặc sẽ được gọi từ component
        // Chỉ fetch nếu chưa có user
        if (!store.getState().auth.user) {
          fetchProfile();
        }
        return true;
      }

      // Token hết hạn - kiểm tra refresh token
      // KHÔNG refresh ngay ở đây, để axios interceptor tự động xử lý khi gặp 401
      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        clearData();
        return false;
      }

      // Refresh token còn hạn - chỉ set state, KHÔNG gọi refresh API
      // Axios interceptor sẽ tự động refresh khi API call thực sự bị 401
      // Đồng bộ customerId và kycStatus từ localStorage vào Redux khi app khởi động lại
      const customerId = getCustomerId();
      const kycStatus = getKycStatus();
      if (customerId) {
        dispatch(setCustomerIdAction(customerId));
      }
      dispatch(
        setDataAndTokenAction({
          isAuthenticated: true,
          token, // Token cũ (đã hết hạn) nhưng vẫn set để axios interceptor xử lý
          refreshToken,
          kycStatus: kycStatus as kycStatusType | null,
        }),
      );
      // Không cần fetch profile ở đây vì đã có trong login flow hoặc sẽ được gọi từ component
      // Chỉ fetch nếu chưa có user
      if (!store.getState().auth.user) {
        fetchProfile();
      }

      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      clearData();
      return false;
    }
  }, [clearData, dispatch, fetchProfile]);

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string, newPasswordConfirmation: string) => {
      const { succeeded } = await caller(
        () =>
          api.patch(`/v1/api/customer/change-password`, {
            oldPassword,
            newPassword,
            newPasswordConfirmation,
          }),
        {
          loadingKey: 'change-password-loading',
          successMessage: 'Password changed successfully',
        },
      );

      return { succeeded };
    },
    [api, caller],
  );

  const forgotPassword = useCallback(
    async (email: string) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/v3/api/auth/customer/forgot-password`, {
            email,
          }),
        {
          loadingKey: 'forgot-password-loading',
          successMessage: 'Password reset code has been sent to your email',
        },
      );

      if (succeeded && content?.resetToken) {
        setResetToken(content.resetToken);
      }

      return {
        succeeded,
        resetToken: content?.resetToken,
        email: content?.email,
        message: content?.message,
        expiresInMinutes: content?.expiresInMinutes,
      };
    },
    [api, caller],
  );

  const verifyForgotPasswordOtp = useCallback(
    async (otpCode: string) => {
      const resetToken = getResetToken();

      if (!resetToken) {
        throw new Error('Reset token not found');
      }

      const { content, succeeded } = await caller(
        () =>
          api.post(`/v3/api/auth/customer/forgot-password/verify-otp`, {
            resetToken,
            otpCode,
          }),
        {
          loadingKey: 'verify-otp-loading',
          successMessage: 'OTP verified successfully',
        },
      );

      if (succeeded && content?.passwordResetToken) {
        // Xóa resetToken cũ và lưu passwordResetToken mới
        removeResetToken();
        setResetToken(content.passwordResetToken);
      }

      return {
        succeeded,
        passwordResetToken: content?.passwordResetToken,
        message: content?.message,
        expiresInMinutes: content?.expiresInMinutes,
      };
    },
    [api, caller],
  );

  const resetPassword = useCallback(
    async (newPassword: string, newPasswordConfirmation: string) => {
      const passwordResetToken = getResetToken();

      if (!passwordResetToken) {
        throw new Error('Password reset token not found');
      }

      const { content, succeeded } = await caller(
        () =>
          api.post(`/v3/api/auth/customer/forgot-password/reset`, {
            passwordResetToken,
            newPassword,
            newPasswordConfirmation,
          }),
        {
          loadingKey: 'reset-password-loading',
          successMessage: 'Your password has been reset successfully',
        },
      );

      if (succeeded) {
        // Xóa passwordResetToken sau khi reset thành công
        removeResetToken();
      }

      return {
        succeeded,
        userId: content?.userId,
        email: content?.email,
        message: content?.message,
      };
    },
    [api, caller],
  );

  const logout = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // Gọi API logout để invalidate refresh token ở server
        await caller(
          () =>
            api.post(`/v1/identity/auth/logout`, {
              refreshToken: refreshToken,
            }),
          {
            loadingKey: 'logout-loading',
            successMessage: Messages.LOGOUT_SUCCESS,
          },
        );
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Vẫn tiếp tục clear data ở client dù API call thất bại
    } finally {
      // Luôn clear data ở client sau khi logout
      clearData();
    }
  }, [api, caller, clearData]);

  return {
    identityLogin,
    verifyIdentityLoginMfa,
    requestIdentityOtp,
    resendIdentityLoginMfa,
    requestPasswordResetOtp,
    verifyIdentityEmail,
    resetIdentityPassword,
    login,
    loginCapcha,
    loginCapchaV3,
    verifyTwoFactor,
    resendOtp,
    register,
    registerCapcha,
    changePassword,
    forgotPassword,
    verifyForgotPasswordOtp,
    resetPassword,
    logout,
    clearData,
    checkAuthStatus,
    isAuthenticated,
    isLoading,
    fetchProfile,
    inviteRegister,
  };
};

// Export global refresh function để sử dụng ở nơi khác
export { globalRefreshToken };
